import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import ManageEmailsContainer from '../../../../../shared/components/v3/ManageEmailsContainer/ManageEmailsContainer';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { ARROW_RIGHT_ICON } from '../../../../../shared/constants/images';
import {
  CLICK_ACTION,
  COLLABORATOR_UPLOAD_METHOD_CHOICE_DICT,
  INVITE_COLLABORATORS_CONFIRM_NEW_RECORDS
} from '../../../../../shared/constants/tagManager';
import { COLLABORATOR_LIMIT_ERROR_TOAST_NE, GENERIC_ERROR_TOAST } from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { ICollaboratorsEmailData } from '../../../../../shared/interfaces/collaborators';
import { EmailsData } from '../../../../../shared/interfaces/manageCollaborators';
import { ICompanyPlan } from '../../../../../shared/interfaces/plans';
import { IToast } from '../../../../../shared/interfaces/toast';
import Companies from '../../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../shared/translations/manageCollaborators';
import SubscriptionCollaboratorsHeadingContainer from '../HeadingContainer/SubscriptionCollaboratorsHeadingContainer';
import './SubscriptionByEmails.scss';

interface ISubscriptionByEmails {
  planSelected: ICompanyPlan;
  setEmailsToUpload: React.Dispatch<React.SetStateAction<EmailsData>>;
  emailsData: EmailsData;
  nextStep: () => void;
}

const SubscriptionByEmails = ({ planSelected, setEmailsToUpload, nextStep, emailsData }: ISubscriptionByEmails) => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<ICollaboratorsEmailData[]>([]);
  const [existingEmails, setExistingEmails] = useState<string[]>([]);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [toastData, setToastData] = useState<IToast>();
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const { company } = useContext(CompanyContext);

  const verifyEmails = useCallback(async () => {
    try {
      setExistingEmails([]);
      setExistingEmails([]);
      setLoading(true);

      const emailsToSend = emails.map((email) => email.emailString);
      const response = await Companies.validateMembers(company.id, emailsToSend);

      if (response.error) {
        setExistingEmails([]);
        setToastData(COLLABORATOR_LIMIT_ERROR_TOAST_NE);
        setOpenToast(true);
      } else {
        setEmailsToUpload({
          emailsToUpload: [...response.missingEmails],
          emailsOmmited: response.existingEmails
        });
        TagManagerService.pushEvent(
          CLICK_ACTION,
          INVITE_COLLABORATORS_CONFIRM_NEW_RECORDS,
          COLLABORATOR_UPLOAD_METHOD_CHOICE_DICT.uploadByEmail
        );
        nextStep();
      }
    } catch {
      setOpenToast(true);
      setToastData(GENERIC_ERROR_TOAST);
    } finally {
      setLoading(false);
    }
  }, [emails, company.id, setEmailsToUpload, nextStep]);

  const isEmailInvalid = useMemo(() => {
    return !emails.length || emails.some((email) => !email.isValidEmail);
  }, [emails]);

  useEffect(() => {
    setDisableButton(isEmailInvalid || loading);
  }, [isEmailInvalid, loading]);

  const handleChangeEmails = useCallback((updatedEmails: ICollaboratorsEmailData[]) => {
    setEmails(updatedEmails);
  }, []);

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  useEffect(() => {
    if (emailsData.emailsToUpload.length > 0 || emailsData.emailsOmmited.length > 0) {
      const mapEmails = (emails: string[]) =>
        emails.map((email) => ({
          emailString: email,
          isValidEmail: true,
          id: email
        }));

      const validatedEmails = mapEmails(emailsData.emailsToUpload as string[]);
      const validatedEmailsOmmited = mapEmails(emailsData.emailsOmmited);

      setEmails([...validatedEmails, ...validatedEmailsOmmited]);
    }
  }, [emailsData]);

  return (
    <>
      <div className="upload-collaborators-section flex flex-col w-full">
        <h3 className="title">{t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.uploadByEmails.title)}</h3>
        <BdsCardContainer paddingSize="padding-l" borderRadiusSize="radius-l" className="text-area-container">
          <SubscriptionCollaboratorsHeadingContainer
            emailsCounter={emails.length}
            loading={loading}
            planSelected={planSelected}
          />
          <ManageEmailsContainer
            initialEmails={emails}
            errorEmails={[]}
            handleChangeEmails={handleChangeEmails}
            loading={loading}
            hideNonErrorTags={loading}
            type="upload-collaborators"
          />
        </BdsCardContainer>
      </div>
      <div className="manage-collaborators-button">
        <BdsButton
          variant="primary"
          size="l"
          action={verifyEmails}
          disabled={disableButton}
          loading={loading}
          iconRight={ARROW_RIGHT_ICON}
        >
          {t(TRANSLATION_MANAGE_COLLABORATORS.buttonNext)}
        </BdsButton>
      </div>
      <Toast
        title={t(toastData?.title || '', { users: existingEmails.length })}
        description={t(toastData?.description || '', { users: existingEmails.length })}
        icon={toastData?.icon || 'error'}
        open={openToast}
        button={toastData?.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default SubscriptionByEmails;
